// Punto de entrada único de las islas React.
//
// Una "isla" es un componente en app/frontend/islands/ montado dentro de una
// vista ERB mediante el helper `react_component` (ApplicationHelper), que genera:
//
//   <div data-island="NombreComponente">
//     <script type="application/json" data-props>{...}</script>
//     <div data-island-root></div>
//   </div>
//
// El JSON de props va en un <script type="application/json"> porque no es JS
// ejecutable: la CSP estricta del proyecto no lo bloquea. React se monta en el
// div interno (data-island-root) y NUNCA sobre el contenedor: así el script de
// props sobrevive en el snapshot que Turbo cachea y la isla puede re-montarse
// al restaurar la página.
import { StrictMode, createElement, type ComponentType } from "react"
import { createRoot, type Root } from "react-dom/client"

type IslandModule = { default: ComponentType<Record<string, unknown>> }

const loaders = import.meta.glob<IslandModule>(["../islands/**/*.tsx", "!**/*.test.tsx"])

// "../islands/HealthDashboard.tsx" -> "HealthDashboard"
const registry = new Map<string, () => Promise<IslandModule>>()
for (const [path, loader] of Object.entries(loaders)) {
  const name = path.split("/").pop()!.replace(/\.tsx$/, "")
  registry.set(name, loader)
}

const activeRoots = new Map<Element, Root>()

function readProps(container: Element): Record<string, unknown> {
  const script = container.querySelector<HTMLScriptElement>("script[data-props]")
  if (!script?.textContent) return {}
  try {
    return JSON.parse(script.textContent)
  } catch (error) {
    console.error(`[islands] Props JSON inválido en isla "${container.getAttribute("data-island")}"`, error)
    return {}
  }
}

async function mountIsland(container: Element) {
  if (activeRoots.has(container)) return

  const name = container.getAttribute("data-island")!
  const loader = registry.get(name)
  if (!loader) {
    console.error(`[islands] No existe la isla "${name}". Islas disponibles: ${[...registry.keys()].join(", ")}`)
    return
  }

  const target = container.querySelector<HTMLElement>("[data-island-root]")
  if (!target) {
    console.error(`[islands] Isla "${name}" sin <div data-island-root> (¿se generó sin el helper react_component?)`)
    return
  }

  const props = readProps(container)
  const { default: Component } = await loader()

  // Entre el await y aquí Turbo pudo desechar la página; si el contenedor ya
  // no está en el documento, no montar sobre un nodo huérfano.
  if (!container.isConnected || activeRoots.has(container)) return

  const root = createRoot(target)
  root.render(createElement(StrictMode, null, createElement(Component, props)))
  activeRoots.set(container, root)
}

function mountAll() {
  // Limpieza: si un contenedor permanente quedó fuera del documento (la nueva
  // página no lo incluía), su root ya no puede vivir — desmontar y olvidar.
  activeRoots.forEach((root, container) => {
    if (!container.isConnected) {
      root.unmount()
      activeRoots.delete(container)
    }
  })
  document.querySelectorAll("[data-island]").forEach((container) => void mountIsland(container))
}

function unmountAll() {
  activeRoots.forEach((root, container) => {
    // Las islas dentro de un contenedor data-turbo-permanent sobreviven a la
    // navegación (Turbo conserva el nodo, p. ej. el widget de ZafIA): no se
    // desmontan y mountIsland las salta en el próximo turbo:load porque su
    // root sigue registrado.
    if (container.closest("[data-turbo-permanent]")) return
    root.unmount()
    activeRoots.delete(container)
  })
}

// turbo:load cubre la carga inicial y cada navegación Turbo. before-cache
// desmonta para que el snapshot cacheado no contenga un árbol React "muerto".
// turbo:frame-load monta islas que llegan dentro de un <turbo-frame> perezoso
// (p. ej. el wizard de agendamiento en el modal del perfil del médico).
document.addEventListener("turbo:load", mountAll)
document.addEventListener("turbo:frame-load", mountAll)
document.addEventListener("turbo:before-cache", unmountAll)

// Fallback para páginas sin Turbo (p. ej. data-turbo="false" o errores estáticos).
if (document.readyState !== "loading") {
  mountAll()
} else {
  document.addEventListener("DOMContentLoaded", mountAll, { once: true })
}
